import { CUBE_SIDE_NAMES } from "../../components/Сube/constants";
import { CubeSideName } from "../../components/Сube/types";
import { ValuesPerSide } from "../useHydratedCubeData/types";
import { CalculatedScoresBySide } from "./types";
import { CalculationType } from "../../types";

export function calculateSideValuesByAverageScore(
  valuesPerSide: Record<CubeSideName, ValuesPerSide>
) {
  return CUBE_SIDE_NAMES.reduce((acc, side) => {
    acc.percentageValue = {
      ...acc.percentageValue,
      [side]: valuesPerSide[side]?.averagePercentageScore ?? 0,
    };

    acc.cubeSideFillArea = {
      ...acc.cubeSideFillArea,
      [side]: valuesPerSide[side]?.averagePercentageScore ?? 0,
    };
    return acc;
  }, {} as CalculatedScoresBySide);
}

export function calculateSideValuesByBenchmarkScore(
  valuesPerSide: Record<CubeSideName, ValuesPerSide>
) {
  return CUBE_SIDE_NAMES.reduce((acc, side) => {
    const percentageValue =
      valuesPerSide[side].averagePercentageScore -
      valuesPerSide[side].benchmarkPercentageScore;

    acc.percentageValue = {
      ...acc.percentageValue,
      [side]: percentageValue,
    };

    acc.cubeSideFillArea = {
      ...acc.cubeSideFillArea,
      [side]: Math.min(100, 100 + percentageValue),
    };

    return acc;
  }, {} as CalculatedScoresBySide);
}

export function calculateBenchmarkPerSide(
  valuesPerSide: Record<CubeSideName, ValuesPerSide>
) {
  return CUBE_SIDE_NAMES.reduce((acc, side) => {
    acc[side] = valuesPerSide[side]?.benchmarkPercentageScore ?? 0;
    return acc;
  }, {} as Record<CubeSideName, number>);
}

export function calculateAverage(
  valuesPerSide: Partial<Record<CubeSideName, ValuesPerSide>>,
  key: keyof Omit<
    ValuesPerSide,
    | "intersectionValues"
    | "aggregatedReport"
    | "consentItems"
    | "qualityItems"
    | "importanceItems"
  >
) {
  const values = Object.values(valuesPerSide);
  const scores = values.map(value => value?.[key]).filter(score => score > 0);
  const sum = scores.reduce((sum, score) => sum + score, 0);
  return scores.length > 0 ? Math.round(sum / scores.length) : 0;
}

export function getAverageOfSides(
  valuesPerSide?: Record<CubeSideName, ValuesPerSide>
) {
  if (!valuesPerSide) {
    return {
      [CalculationType.Benchmark]: calculateSideValuesByAverageScore(
        {} as Record<CubeSideName, ValuesPerSide>
      ),
      [CalculationType.AverageScore]: calculateSideValuesByAverageScore(
        {} as Record<CubeSideName, ValuesPerSide>
      ),
      averageScore: 0,
      averageBenchmark: 0,
      benchmarkPerSidePercentageScore: calculateBenchmarkPerSide(
        {} as Record<CubeSideName, ValuesPerSide>
      ),
    };
  }

  return {
    [CalculationType.Benchmark]:
      calculateSideValuesByBenchmarkScore(valuesPerSide),
    [CalculationType.AverageScore]:
      calculateSideValuesByAverageScore(valuesPerSide),
    averageScore: calculateAverage(valuesPerSide, "averagePercentageScore"),
    averageBenchmark: calculateAverage(
      valuesPerSide,
      "benchmarkPercentageScore"
    ),
    benchmarkPerSidePercentageScore: calculateBenchmarkPerSide(valuesPerSide),
  };
}
